import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Upload, Github, Linkedin } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";

const fade = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: false, amount: 0.1 }, transition: { duration: 0.6 } };
const inputCls = "h-11 rounded-xl border px-3 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full";
const inputStyle = { borderColor: "rgba(226,232,240,0.9)" };
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function CompleteProfile() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        linkedin_url: "", github_url: "",
        years_experience: "", tech_stack: "", desired_role: "", salary_expectation: "", bio: "",
    });
    const [cv, setCv] = useState(null);

    function update(field) {
        return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const token = localStorage.getItem("hirrd_token");
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
            if (cv) fd.append("cv", cv);

            const res = await fetch(`${API}/api/auth/engineer/profile`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Update failed");
            navigate("/engineer/dashboard", { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <FXBackdrop>
            <section className="containerX pt-10 md:pt-16 pb-16">
                <motion.div {...fade} className="max-w-xl mx-auto">
                    <div className="chip">
                        <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} />
                        Complete Your Profile
                    </div>
                    <h1 className="h1 mt-5 text-3xl md:text-4xl">Welcome, {user?.profile?.full_name || "Engineer"}!</h1>
                    <p className="p mt-2">Fill in your profile to get matched to the best roles.</p>

                    <div className="flex gap-2 mt-6 mb-6">
                        {[1, 2].map((s) => (
                            <div key={s} className="flex-1 h-1.5 rounded-full transition-all" style={{
                                background: s <= step ? "rgb(139,92,246)" : "rgba(226,232,240,0.9)"
                            }} />
                        ))}
                    </div>

                    <form onSubmit={handleSubmit} className="glass p-6 md:p-8">
                        {error && (
                            <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>
                        )}

                        {step === 1 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Your profiles</div>
                                <div className="relative">
                                    <Linkedin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input className={inputCls + " pl-9"} style={inputStyle} placeholder="LinkedIn URL" value={form.linkedin_url} onChange={update("linkedin_url")} />
                                </div>
                                <div className="relative">
                                    <Github size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input className={inputCls + " pl-9"} style={inputStyle} placeholder="GitHub URL" value={form.github_url} onChange={update("github_url")} />
                                </div>
                                <div>
                                    <label className="flex items-center gap-3 cursor-pointer rounded-xl border px-4 py-3 hover:bg-white/50 transition" style={inputStyle}>
                                        <Upload size={18} className="text-violet-500" />
                                        <span className="text-sm">{cv ? cv.name : "Upload your CV (PDF, DOC)"}</span>
                                        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files[0])} />
                                    </label>
                                </div>
                                <button type="button" onClick={() => setStep(2)} className="btn btnPrimary justify-center mt-2">
                                    Next <ArrowRight size={16} />
                                </button>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="grid gap-3">
                                <div className="font-semibold text-lg mb-1">Experience & preferences</div>
                                <input className={inputCls} style={inputStyle} placeholder="Years of experience" type="number" value={form.years_experience} onChange={update("years_experience")} />
                                <input className={inputCls} style={inputStyle} placeholder="Tech stack (comma-separated)" value={form.tech_stack} onChange={update("tech_stack")} />
                                <input className={inputCls} style={inputStyle} placeholder="Desired role" value={form.desired_role} onChange={update("desired_role")} />
                                <input className={inputCls} style={inputStyle} placeholder="Salary expectation" value={form.salary_expectation} onChange={update("salary_expectation")} />
                                <textarea className="min-h-20 rounded-xl border px-3 py-2 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full"
                                    style={inputStyle} placeholder="Short bio (optional)" value={form.bio} onChange={update("bio")} />
                                <div className="flex gap-2 mt-2">
                                    <button type="button" onClick={() => setStep(1)} className="btn btnGhost flex-1">Back</button>
                                    <button type="submit" disabled={loading} className="btn btnPrimary flex-1 disabled:opacity-50">
                                        {loading ? "Saving..." : "Save profile"} {!loading && <ArrowRight size={16} />}
                                    </button>
                                </div>
                            </div>
                        )}

                        <button type="button" onClick={() => navigate("/engineer/dashboard")} className="w-full text-center text-sm text-slate-400 mt-4 hover:text-slate-600 transition">
                            Skip for now
                        </button>
                    </form>
                </motion.div>
            </section>
        </FXBackdrop>
    );
}
